import {
  EQUIPMENT,
  equipmentDefinition,
  sameEquipmentKey,
} from "@frontier/content/arsenal";
import type { EquipmentStack } from "@frontier/content/arsenal";
import type { Profile } from "@frontier/protocol/contracts";
import { EquipmentArt } from "./EquipmentArt";
import {
  contextAccepts,
  equipmentContextLabel,
  installedEquipmentSlot,
} from "./equipment-selection";
import type { EquipmentContext } from "./equipment-selection";
import { sameEquipmentFocus } from "./equipment-focus";
import type { EquipmentFocus } from "./equipment-focus";

export function EquipmentInventory({
  context,
  profile,
  selected,
  showBlueprints,
  onInspect,
}: {
  context: EquipmentContext;
  profile: Profile;
  selected: EquipmentFocus | null;
  showBlueprints: boolean;
  onInspect: (focus: EquipmentFocus) => void;
}) {
  const owned = profile.arsenal.stacks.filter(
    (stack) => contextAccepts(context, stack.key) && stack.count > 0,
  );
  const entries: EquipmentStack[] = showBlueprints
    ? [
        ...owned,
        ...EQUIPMENT.filter(
          (item) =>
            contextAccepts(context, item.key) &&
            !owned.some((stack) => sameEquipmentKey(stack.key, item.key)),
        ).map((item) => ({ key: item.key, tier: 0 as const, count: 0 })),
      ]
    : owned;
  return (
    <section
      className="equipment-stock"
      aria-label={`Склад: ${equipmentContextLabel(context)}`}
    >
      <div className="equipment-stock-grid">
        {entries.map((stack) => {
          const item = equipmentDefinition(stack.key);
          const installed = installedEquipmentSlot(
            profile,
            stack.key,
            stack.tier,
          );
          const inspected = sameEquipmentFocus(selected, stack);
          const known = profile.arsenal.knownBlueprints.some((key) =>
            sameEquipmentKey(key, stack.key),
          );
          return (
            <button
              key={`${stack.key.kind}:${stack.key.id}:${stack.tier}`}
              className={`stock-tile ${installed ? "installed" : ""} ${inspected ? "inspected" : ""} ${stack.count === 0 ? "blueprint-tile" : ""}`}
              data-equipment-id={stack.key.id}
              data-equipment-tier={stack.tier}
              data-installed={installed !== null}
              aria-pressed={inspected}
              aria-label={`Осмотреть: ${item.name} MK ${"I".repeat(stack.tier + 1)}`}
              onClick={() => onInspect({ key: stack.key, tier: stack.tier })}
            >
              <span className="stock-art">
                <EquipmentArt equipment={stack.key} />
              </span>
              <span className="stock-grade">
                MK {"I".repeat(stack.tier + 1)}
              </span>
              {stack.count > 0 && (
                <span
                  className="stock-count"
                  title="Всего копий, включая установленные"
                >
                  ×{stack.count}
                </span>
              )}
              <b>{item.name}</b>
              <small>
                {installed
                  ? `НА КОРАБЛЕ · ${installed}`
                  : stack.count > 0
                    ? "НА СКЛАДЕ"
                    : known
                      ? "МОЖНО ИЗГОТОВИТЬ"
                      : "ЧЕРТЁЖ ЗАКРЫТ"}
              </small>
            </button>
          );
        })}
      </div>
      {entries.length === 0 && (
        <p className="stock-empty-hint">
          Нет подходящего оснащения.
        </p>
      )}
    </section>
  );
}
